import { useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import Divider from '@mui/material/Divider';
import LockIcon from '@mui/icons-material/Lock';
import TerminalIcon from '@mui/icons-material/Terminal';
import SettingsIcon from '@mui/icons-material/Settings';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FolderIcon from '@mui/icons-material/Folder';
import Alert from '@mui/material/Alert';
import { PortalServerDetailOut as PortalServerDetail, PortalSkillOut as PortalSkill } from '../types/apiRequests';
import { TranslationKey } from '../i18n/en';
import CodeBlock from './CodeBlock';
import PillSelector from './PillSelector';
import StepBlock from './StepBlock';

type Platform = 'claude' | 'copilot' | 'chatgpt' | 'gemini';
type SubTab = string;

const PLATFORMS: { id: Platform; label: string; color: string; subs: { id: SubTab; label: string }[] }[] = [
  { id: 'claude', label: 'Claude', color: '#D97757', subs: [{ id: 'desktop', label: 'Desktop & Web' }, { id: 'code', label: 'Claude Code' }] },
  { id: 'copilot', label: 'Copilot', color: '#0078d4', subs: [{ id: 'app', label: 'Microsoft 365' }, { id: 'vscode', label: 'VS Code' }] },
  { id: 'chatgpt', label: 'ChatGPT', color: '#10a37f', subs: [{ id: 'app', label: 'ChatGPT App' }] },
  { id: 'gemini', label: 'Gemini', color: '#4285f4', subs: [{ id: 'app', label: 'Gemini App' }, { id: 'cli', label: 'Gemini CLI' }] },
];

function Hint({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, fontSize: '0.85rem', lineHeight: 1.7 }}>
      {children}
    </Typography>
  );
}

function InlineCode({ children }: { children: string }) {
  return <code style={{ background: '#f4f4f5', padding: '2px 6px', borderRadius: 4, fontSize: '0.78rem', fontFamily: "'JetBrains Mono', monospace" }}>{children}</code>;
}

function SkillsList({ skills, copied, onCopy, copyLabel, copiedLabel }: { skills: PortalSkill[]; copied: boolean; onCopy: (t: string) => void; copyLabel?: string; copiedLabel?: string }) {
  if (skills.length === 0) return null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {skills.map((skill) => (
        <Box key={skill.id} sx={{
          'display': 'flex', 'alignItems': 'center', 'justifyContent': 'space-between',
          'p': 1.5, 'borderRadius': '8px', 'border': '1px solid rgba(0,0,0,0.06)',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.015)' },
        }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{skill.name}</Typography>
            {skill.description && <Typography variant="caption" sx={{ color: 'text.disabled' }}>{skill.description}</Typography>}
          </Box>
          <Tooltip title={copied ? (copiedLabel ?? 'Gekopieerd!') : (copyLabel ?? 'Kopieer skill')}>
            <IconButton size="small" onClick={() => onCopy(skill.content)} sx={{ color: 'text.disabled' }}>
              {copied ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
        </Box>
      ))}
    </Box>
  );
}

interface ConnectTabContentProps {
  server: PortalServerDetail;
  serverUrl: string;
  copied: boolean;
  onCopy: (text: string) => void;
  isBeheerder: boolean;
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
}

export default function ConnectTabContent({ server, serverUrl, copied, onCopy, isBeheerder, t }: ConnectTabContentProps) {
  const [platform, setPlatform] = useState<Platform>('claude');
  const platformDef = PLATFORMS.find((p) => p.id === platform)!;
  const [subTab, setSubTab] = useState(platformDef.subs[0].id);

  const handlePlatformChange = (p: Platform) => {
    setPlatform(p);
    setSubTab(PLATFORMS.find((pl) => pl.id === p)!.subs[0].id);
  };

  if (server.access_status !== 'full_access' && !isBeheerder) {
    return (
      <Card sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <LockIcon sx={{ fontSize: 32, color: '#d4d4d8', mb: 2 }} />
          <Typography sx={{ color: 'text.secondary', fontWeight: 500, mb: 1 }}>{t('serverDetail.connect.noAccess.title')}</Typography>
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>{t('serverDetail.connect.noAccess.body')}</Typography>
        </Box>
      </Card>
    );
  }

  const desktopConfig = JSON.stringify({ mcpServers: { [server.name]: { url: serverUrl } } }, null, 2);
  const geminiConfig = JSON.stringify({ mcpServers: { [server.name]: { url: serverUrl, transport: 'http' } } }, null, 2);

  const renderContent = () => {
    // ── Claude Desktop & Web ──
    if (platform === 'claude' && subTab === 'desktop') {
      return (
        <>
          <StepBlock number={1} title={t('serverDetail.connect.step.addServer')} icon={<SettingsIcon />}>
            <Hint>
              {t('serverDetail.connect.claude.desktop.step1.hint')}
            </Hint>
            <CodeBlock onCopy={() => onCopy(serverUrl)} copied={copied} copyLabel={t('common.copy')} copiedLabel={t('common.copied')}>{serverUrl}</CodeBlock>
          </StepBlock>

          <Divider sx={{ my: 1 }} />

          <StepBlock number={2} title={t('serverDetail.connect.step.authenticate')} icon={<LockIcon />}>
            <Hint>
              {t('serverDetail.connect.claude.desktop.step2.hint')}
            </Hint>
          </StepBlock>

          <Divider sx={{ my: 1 }} />

          {server.project_instructions && (
            <>
              <StepBlock number={3} title={t('serverDetail.connect.step.createProject')} icon={<FolderIcon />}>
                <Hint>
                  {t('serverDetail.connect.claude.desktop.step3.hint')}
                </Hint>
                <CodeBlock onCopy={() => onCopy(server.project_instructions!)} copied={copied} copyLabel={t('common.copy')} copiedLabel={t('common.copied')}>{server.project_instructions}</CodeBlock>
              </StepBlock>
              <Divider sx={{ my: 1 }} />
            </>
          )}

          {(server.skills ?? []).length > 0 && (
            <StepBlock number={server.project_instructions ? 4 : 3} title={t('serverDetail.connect.step.addCustomInstructions')} icon={<AutoAwesomeIcon />}>
              <Hint>
                {t('serverDetail.connect.claude.desktop.step4.hint')}
              </Hint>
              <SkillsList skills={server.skills ?? []} copied={copied} onCopy={onCopy} copyLabel={t('serverDetail.copySkill')} copiedLabel={t('common.copied')} />
            </StepBlock>
          )}
        </>
      );
    }

    // ── Claude Code ──
    if (platform === 'claude' && subTab === 'code') {
      return (
        <>
          <StepBlock number={1} title={t('serverDetail.connect.step.addServer')} icon={<TerminalIcon />}>
            <Hint>{t('serverDetail.connect.step.runCommand')}</Hint>
            <CodeBlock onCopy={() => onCopy(`claude mcp add --transport http ${server.name} "${serverUrl}"`)} copied={copied} copyLabel={t('common.copy')} copiedLabel={t('common.copied')}>
              {`claude mcp add --transport http ${server.name} \\\n  "${serverUrl}"`}
            </CodeBlock>
            <Typography variant="body2" sx={{ color: 'text.disabled', mt: 1.5, fontSize: '0.8rem' }}>
              {t('serverDetail.connect.claude.code.step1.altHint')} <InlineCode>claude_desktop_config.json</InlineCode>:
            </Typography>
            <Box sx={{ mt: 1 }}>
              <CodeBlock onCopy={() => onCopy(desktopConfig)} copied={copied} copyLabel={t('common.copy')} copiedLabel={t('common.copied')}>{desktopConfig}</CodeBlock>
            </Box>
          </StepBlock>

          <Divider sx={{ my: 1 }} />

          <StepBlock number={2} title={t('serverDetail.connect.step.authenticate')} icon={<LockIcon />}>
            <Hint>
              {t('serverDetail.connect.claude.code.step2.hint')}
            </Hint>
          </StepBlock>

          <Divider sx={{ my: 1 }} />

          {server.project_instructions && (
            <>
              <StepBlock number={3} title={t('serverDetail.connect.claude.code.step3.title')} icon={<FolderIcon />}>
                <Hint>
                  {t('serverDetail.connect.claude.code.step3.hint')}
                </Hint>
                <CodeBlock onCopy={() => onCopy(server.project_instructions!)} copied={copied} copyLabel={t('common.copy')} copiedLabel={t('common.copied')}>{server.project_instructions}</CodeBlock>
              </StepBlock>
              <Divider sx={{ my: 1 }} />
            </>
          )}

          {(server.skills ?? []).length > 0 && (
            <StepBlock number={server.project_instructions ? 4 : 3} title={t('serverDetail.connect.step.addSkills')} icon={<AutoAwesomeIcon />}>
              <Hint>
                {t('serverDetail.connect.claude.code.step4.hint')}
              </Hint>
              <SkillsList skills={server.skills ?? []} copied={copied} onCopy={onCopy} copyLabel={t('serverDetail.copySkill')} copiedLabel={t('common.copied')} />
            </StepBlock>
          )}
        </>
      );
    }

    // ── Copilot (Microsoft 365) ──
    if (platform === 'copilot' && subTab === 'app') {
      return (
        <>
          <StepBlock number={1} title={t('serverDetail.connect.step.addServer')} icon={<SettingsIcon />}>
            <Hint>{t('serverDetail.connect.copilot.app.step1.hint')}</Hint>
            <CodeBlock onCopy={() => onCopy(serverUrl)} copied={copied} copyLabel={t('common.copy')} copiedLabel={t('common.copied')}>{serverUrl}</CodeBlock>
          </StepBlock>

          <Divider sx={{ my: 1 }} />

          <StepBlock number={2} title={t('serverDetail.connect.step.authenticate')} icon={<LockIcon />}>
            <Hint>
              {t('serverDetail.connect.copilot.app.step2.hint')}
            </Hint>
          </StepBlock>

          <Divider sx={{ my: 1 }} />

          {server.project_instructions && (
            <>
              <StepBlock number={3} title={t('serverDetail.connect.step.createProject')} icon={<FolderIcon />}>
                <Hint>
                  {t('serverDetail.connect.copilot.app.step3.hint')}
                </Hint>
                <CodeBlock onCopy={() => onCopy(server.project_instructions!)} copied={copied} copyLabel={t('common.copy')} copiedLabel={t('common.copied')}>{server.project_instructions}</CodeBlock>
              </StepBlock>
              <Divider sx={{ my: 1 }} />
            </>
          )}

          {(server.skills ?? []).length > 0 && (
            <StepBlock number={server.project_instructions ? 4 : 3} title={t('serverDetail.connect.step.addCustomInstructions')} icon={<AutoAwesomeIcon />}>
              <Hint>
                {t('serverDetail.connect.copilot.app.step4.hint')}
              </Hint>
              <SkillsList skills={server.skills ?? []} copied={copied} onCopy={onCopy} copyLabel={t('serverDetail.copySkill')} copiedLabel={t('common.copied')} />
            </StepBlock>
          )}
        </>
      );
    }

    // ── Copilot (VS Code) ──
    if (platform === 'copilot' && subTab === 'vscode') {
      const copilotConfig = JSON.stringify({ servers: { [server.name]: { type: 'http', url: serverUrl } } }, null, 2);
      return (
        <>
          <StepBlock number={1} title={t('serverDetail.connect.step.addServer')} icon={<SettingsIcon />}>
            <Hint>{t('serverDetail.connect.copilot.vscode.step1.hint')}</Hint>
            <CodeBlock onCopy={() => onCopy(copilotConfig)} copied={copied} copyLabel={t('common.copy')} copiedLabel={t('common.copied')}>{copilotConfig}</CodeBlock>
          </StepBlock>

          <Divider sx={{ my: 1 }} />

          <StepBlock number={2} title={t('serverDetail.connect.step.authenticate')} icon={<LockIcon />}>
            <Hint>
              {t('serverDetail.connect.copilot.vscode.step2.hint')}
            </Hint>
          </StepBlock>

          <Divider sx={{ my: 1 }} />

          {server.project_instructions && (
            <>
              <StepBlock number={3} title={t('serverDetail.connect.step.createProject')} icon={<FolderIcon />}>
                <Hint>
                  {t('serverDetail.connect.copilot.vscode.step3.hint')}
                </Hint>
                <CodeBlock onCopy={() => onCopy(server.project_instructions!)} copied={copied} copyLabel={t('common.copy')} copiedLabel={t('common.copied')}>{server.project_instructions}</CodeBlock>
              </StepBlock>
              <Divider sx={{ my: 1 }} />
            </>
          )}

          {(server.skills ?? []).length > 0 && (
            <StepBlock number={server.project_instructions ? 4 : 3} title={t('serverDetail.connect.step.addCustomInstructions')} icon={<AutoAwesomeIcon />}>
              <Hint>
                {t('serverDetail.connect.copilot.vscode.step4.hint')}
              </Hint>
              <SkillsList skills={server.skills ?? []} copied={copied} onCopy={onCopy} copyLabel={t('serverDetail.copySkill')} copiedLabel={t('common.copied')} />
            </StepBlock>
          )}
        </>
      );
    }

    // ── ChatGPT App ──
    if (platform === 'chatgpt') {
      return (
        <>
          <StepBlock number={1} title={t('serverDetail.connect.step.addServer')} icon={<SettingsIcon />}>
            <Hint>
              {t('serverDetail.connect.chatgpt.step1.hint')}
            </Hint>
            <CodeBlock onCopy={() => onCopy(serverUrl)} copied={copied} copyLabel={t('common.copy')} copiedLabel={t('common.copied')}>{serverUrl}</CodeBlock>
          </StepBlock>

          <Divider sx={{ my: 1 }} />

          <StepBlock number={2} title={t('serverDetail.connect.step.authenticate')} icon={<LockIcon />}>
            <Hint>
              {t('serverDetail.connect.chatgpt.step2.hint')}
            </Hint>
          </StepBlock>

          <Divider sx={{ my: 1 }} />

          {server.project_instructions && (
            <>
              <StepBlock number={3} title={t('serverDetail.connect.step.createProject')} icon={<FolderIcon />}>
                <Hint>
                  {t('serverDetail.connect.chatgpt.step3.hint')}
                </Hint>
                <CodeBlock onCopy={() => onCopy(server.project_instructions!)} copied={copied} copyLabel={t('common.copy')} copiedLabel={t('common.copied')}>{server.project_instructions}</CodeBlock>
              </StepBlock>
              <Divider sx={{ my: 1 }} />
            </>
          )}

          {(server.skills ?? []).length > 0 && (
            <StepBlock number={server.project_instructions ? 4 : 3} title={t('serverDetail.connect.step.addCustomInstructions')} icon={<AutoAwesomeIcon />}>
              <Hint>
                {t('serverDetail.connect.chatgpt.step4.hint')}
              </Hint>
              <SkillsList skills={server.skills ?? []} copied={copied} onCopy={onCopy} copyLabel={t('serverDetail.copySkill')} copiedLabel={t('common.copied')} />
            </StepBlock>
          )}
        </>
      );
    }

    // ── Gemini App ──
    if (platform === 'gemini' && subTab === 'app') {
      return (
        <>
          <StepBlock number={1} title={t('serverDetail.connect.gemini.app.step1.title')} icon={<SettingsIcon />}>
            <Hint>
              {t('serverDetail.connect.gemini.app.step1.hint')}
            </Hint>
            <CodeBlock onCopy={() => onCopy(serverUrl)} copied={copied} copyLabel={t('common.copy')} copiedLabel={t('common.copied')}>{serverUrl}</CodeBlock>
          </StepBlock>

          <Divider sx={{ my: 1 }} />

          {server.project_instructions && (
            <>
              <StepBlock number={2} title={t('serverDetail.connect.gemini.app.step2.title')} icon={<FolderIcon />}>
                <Hint>
                  {t('serverDetail.connect.gemini.app.step2.hint')}
                </Hint>
                <CodeBlock onCopy={() => onCopy(server.project_instructions!)} copied={copied} copyLabel={t('common.copy')} copiedLabel={t('common.copied')}>{server.project_instructions}</CodeBlock>
              </StepBlock>
              <Divider sx={{ my: 1 }} />
            </>
          )}

          {(server.skills ?? []).length > 0 && (
            <StepBlock number={server.project_instructions ? 3 : 2} title={t('serverDetail.connect.step.addCustomInstructions')} icon={<AutoAwesomeIcon />}>
              <Hint>
                {t('serverDetail.connect.gemini.app.step3.hint')}
              </Hint>
              <SkillsList skills={server.skills ?? []} copied={copied} onCopy={onCopy} copyLabel={t('serverDetail.copySkill')} copiedLabel={t('common.copied')} />
            </StepBlock>
          )}
        </>
      );
    }

    // ── Gemini CLI ──
    if (platform === 'gemini' && subTab === 'cli') {
      return (
        <>
          <StepBlock number={1} title={t('serverDetail.connect.step.addServer')} icon={<TerminalIcon />}>
            <Hint>{t('serverDetail.connect.step.runCommand')}</Hint>
            <CodeBlock onCopy={() => onCopy(`gemini mcp add --transport http ${server.name} "${serverUrl}"`)} copied={copied} copyLabel={t('common.copy')} copiedLabel={t('common.copied')}>
              {`gemini mcp add --transport http ${server.name} \\\n  "${serverUrl}"`}
            </CodeBlock>
            <Typography variant="body2" sx={{ color: 'text.disabled', mt: 1.5, fontSize: '0.8rem' }}>
              {t('serverDetail.connect.gemini.cli.step1.altHint')}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <CodeBlock onCopy={() => onCopy(geminiConfig)} copied={copied} copyLabel={t('common.copy')} copiedLabel={t('common.copied')}>{geminiConfig}</CodeBlock>
            </Box>
          </StepBlock>

          <Divider sx={{ my: 1 }} />

          <StepBlock number={2} title={t('serverDetail.connect.step.authenticate')} icon={<LockIcon />}>
            <Hint>
              {t('serverDetail.connect.gemini.cli.step2.hint')}
            </Hint>
          </StepBlock>

          <Divider sx={{ my: 1 }} />

          {server.project_instructions && (
            <>
              <StepBlock number={3} title={t('serverDetail.connect.gemini.cli.step3.title')} icon={<FolderIcon />}>
                <Hint>
                  {t('serverDetail.connect.gemini.cli.step3.hint')}
                </Hint>
                <CodeBlock onCopy={() => onCopy(server.project_instructions!)} copied={copied} copyLabel={t('common.copy')} copiedLabel={t('common.copied')}>{server.project_instructions}</CodeBlock>
              </StepBlock>
              <Divider sx={{ my: 1 }} />
            </>
          )}

          {(server.skills ?? []).length > 0 && (
            <StepBlock number={server.project_instructions ? 4 : 3} title={t('serverDetail.connect.step.addSkills')} icon={<AutoAwesomeIcon />}>
              <Hint>
                {t('serverDetail.connect.gemini.cli.step4.hint')}
              </Hint>
              <SkillsList skills={server.skills ?? []} copied={copied} onCopy={onCopy} copyLabel={t('serverDetail.copySkill')} copiedLabel={t('common.copied')} />
            </StepBlock>
          )}
        </>
      );
    }

    return null;
  };

  return (
    <Card sx={{ overflow: 'hidden' }}>
      {server.access_status !== 'full_access' && isBeheerder && (
        <Alert severity="info" sx={{ m: 3, mb: 0 }}>
          {t('serverDetail.connect.inactiveAlert')}
        </Alert>
      )}
      <Box sx={{ p: 3, pb: 0, display: 'flex', flexDirection: 'column', gap: 1.5, borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
        <PillSelector
          items={PLATFORMS.map((p) => ({ id: p.id, label: p.label }))}
          value={platform}
          onChange={(v) => handlePlatformChange(v as Platform)}
          color={platformDef.color}
        />
        {platformDef.subs.length > 1 && (
          <Box sx={{ display: 'flex', gap: 0, borderBottom: '1px solid transparent', mb: -0.25 }}>
            {platformDef.subs.map((sub) => (
              <Box
                key={sub.id}
                onClick={() => setSubTab(sub.id)}
                sx={{
                  px: 2, py: 1, cursor: 'pointer',
                  fontSize: '0.8rem', fontWeight: subTab === sub.id ? 600 : 500,
                  color: subTab === sub.id ? 'text.primary' : 'text.disabled',
                  borderBottom: subTab === sub.id ? `2px solid ${platformDef.color}` : '2px solid transparent',
                  transition: 'all 0.15s ease',
                  '&:hover': { color: 'text.primary' },
                }}
              >
                {sub.label}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Box sx={{ p: 4 }}>{renderContent()}</Box>
    </Card>
  );
}
